import { ListFilter, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useChatDepartments } from '../hooks/use-chat-departments';
import {
  activeFilterCount,
  INBOX_KINDS,
  INBOX_RATINGS,
  inboxView,
  type InboxSearch,
} from '../schemas/inbox-search';

const ANY = '__any';

export interface InboxFiltersProps {
  search: InboxSearch;
  onSearchChange: (patch: Partial<InboxSearch>) => void;
  /** Stores the member can see; the store filter shows only on the org-wide inbox. */
  stores: ReadonlyArray<{ id: string; name: string }>;
  pinnedStoreId?: string | undefined;
}

/** Search box plus a filter popover; every value lives in the URL. */
export function InboxFilters({ search, onSearchChange, stores, pinnedStoreId }: InboxFiltersProps) {
  const { t } = useTranslation('conversations');
  const [text, setText] = useState(search.q ?? '');
  const settled = useDebouncedValue(text.trim(), 300);
  const pinned = pinnedStoreId !== undefined;
  // The Tickets view pins the kind, so the kind filter would only contradict it.
  const kindFilter = inboxView(search) !== 'tickets';
  // Departments belong to one store: offered only when the list is narrowed to one.
  const departmentStoreId =
    pinnedStoreId ?? (search.storeId && !search.storeId.includes(',') ? search.storeId : undefined);
  const departments = useChatDepartments(departmentStoreId);

  useEffect(() => {
    if ((search.q ?? '') !== settled) onSearchChange({ q: settled || undefined });
    // only the settled text drives the URL; the URL never overwrites what is being typed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  const count = activeFilterCount({ ...search, q: undefined }, pinned);
  const storeName = (id: string) => stores.find((store) => store.id === id)?.name ?? id;
  const departmentName = (id: string) => departments.data?.find((d) => d.id === id)?.name ?? id;

  const chips: Array<{ key: keyof InboxSearch; label: string }> = [];
  if (search.assignedTo) {
    chips.push({
      key: 'assignedTo',
      label:
        search.assignedTo === 'me'
          ? t('filters.mine')
          : search.assignedTo === 'none'
            ? t('filters.unassigned')
            : t('filters.assignee'),
    });
  }
  if (kindFilter && search.kind) chips.push({ key: 'kind', label: t(`kind.${search.kind}`) });
  if (search.rating) chips.push({ key: 'rating', label: t(`rating.${search.rating}`) });
  if (search.missed !== undefined)
    chips.push({
      key: 'missed',
      label: t(search.missed ? 'filters.missedOnly' : 'filters.notMissed'),
    });
  if (search.slaBreached !== undefined)
    chips.push({
      key: 'slaBreached',
      label: t(search.slaBreached ? 'filters.slaBreached' : 'filters.slaOk'),
    });
  if (search.tag) chips.push({ key: 'tag', label: `#${search.tag}` });
  if (search.departmentId)
    chips.push({ key: 'departmentId', label: departmentName(search.departmentId) });
  if (!pinned && search.storeId)
    chips.push({ key: 'storeId', label: search.storeId.split(',').map(storeName).join(', ') });

  return (
    <div className="space-y-2 border-b px-3 py-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            type="search"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={t('filters.search')}
            aria-label={t('filters.search')}
            className="h-8 pl-8"
            maxLength={120}
          />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8">
              <ListFilter aria-hidden />
              {t('filters.open')}
              {count > 0 ? <Badge className="h-4 px-1 text-[10px]">{count}</Badge> : null}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 space-y-3">
            <FilterSelect
              id="inbox-assignee"
              label={t('filters.assignee')}
              value={
                search.assignedTo === 'me' || search.assignedTo === 'none'
                  ? search.assignedTo
                  : undefined
              }
              options={[
                { value: 'me', label: t('filters.mine') },
                { value: 'none', label: t('filters.unassigned') },
              ]}
              onChange={(value) => onSearchChange({ assignedTo: value })}
            />
            {kindFilter ? (
              <FilterSelect
                id="inbox-kind"
                label={t('filters.kind')}
                value={search.kind}
                options={INBOX_KINDS.map((kind) => ({ value: kind, label: t(`kind.${kind}`) }))}
                onChange={(value) => onSearchChange({ kind: value as InboxSearch['kind'] })}
              />
            ) : null}
            <FilterSelect
              id="inbox-rating"
              label={t('filters.rating')}
              value={search.rating}
              options={INBOX_RATINGS.map((rating) => ({
                value: rating,
                label: t(`rating.${rating}`),
              }))}
              onChange={(value) => onSearchChange({ rating: value as InboxSearch['rating'] })}
            />
            {!pinned && stores.length > 1 ? (
              <FilterSelect
                id="inbox-store"
                label={t('filters.store')}
                value={search.storeId && !search.storeId.includes(',') ? search.storeId : undefined}
                options={stores.map((store) => ({ value: store.id, label: store.name }))}
                onChange={(value) => onSearchChange({ storeId: value, departmentId: undefined })}
              />
            ) : null}
            {departmentStoreId && (departments.data?.length ?? 0) > 0 ? (
              <FilterSelect
                id="inbox-department"
                label={t('filters.department')}
                value={search.departmentId}
                options={(departments.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
                onChange={(value) => onSearchChange({ departmentId: value })}
              />
            ) : null}
            <TagFilter value={search.tag} onChange={(tag) => onSearchChange({ tag })} />
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="inbox-missed">{t('filters.missedOnly')}</Label>
              <Switch
                id="inbox-missed"
                checked={search.missed === true}
                onCheckedChange={(checked) =>
                  onSearchChange({ missed: checked ? true : undefined })
                }
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="inbox-sla">{t('filters.slaBreached')}</Label>
              <Switch
                id="inbox-sla"
                checked={search.slaBreached === true}
                onCheckedChange={(checked) =>
                  onSearchChange({ slaBreached: checked ? true : undefined })
                }
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1">
          {chips.map((chip) => (
            <Badge key={chip.key} variant="secondary" className="gap-1 pr-1 font-normal">
              {chip.label}
              <button
                type="button"
                className="hover:bg-muted rounded-sm"
                aria-label={t('filters.remove', { filter: chip.label })}
                onClick={() => onSearchChange({ [chip.key]: undefined })}
              >
                <X className="size-3" aria-hidden />
              </button>
            </Badge>
          ))}
          <Button
            variant="link"
            size="sm"
            className="h-6 px-1 text-xs"
            onClick={() =>
              onSearchChange({
                assignedTo: undefined,
                kind: undefined,
                rating: undefined,
                missed: undefined,
                slaBreached: undefined,
                tag: undefined,
                departmentId: undefined,
                ...(pinned ? {} : { storeId: undefined }),
              })
            }
          >
            {t('filters.clear')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function FilterSelect({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string | undefined;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string | undefined) => void;
}) {
  const { t } = useTranslation('conversations');
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value ?? ANY}
        onValueChange={(next) => onChange(next === ANY ? undefined : next)}
      >
        <SelectTrigger id={id} className="h-8 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>{t('filters.any')}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function TagFilter({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (tag: string | undefined) => void;
}) {
  const { t } = useTranslation('conversations');
  const [draft, setDraft] = useState(value ?? '');
  const commit = () => onChange(draft.trim() || undefined);
  return (
    <div className="grid gap-1.5">
      <Label htmlFor="inbox-tag">{t('filters.tag')}</Label>
      <Input
        id="inbox-tag"
        value={draft}
        maxLength={60}
        className="h-8"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit();
        }}
      />
    </div>
  );
}
